import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api/axios';

export default function PatientRecords() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get('/medical-records/mine').then(r => setRecords(r.data))
      .finally(() => setLoading(false));
  }, []);

  if (selected) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-white border-b px-4 py-3 flex items-center gap-3 sticky top-0 z-10">
          <button onClick={() => setSelected(null)} className="text-green-600 font-medium">← Retour</button>
          <h1 className="font-bold text-gray-800">Dossier médical</h1>
        </div>
        <div className="max-w-lg mx-auto p-4 space-y-4">
          <div className="card">
            <p className="text-xs text-gray-400 mb-1">{new Date(selected.created_at).toLocaleDateString('fr-FR', {day:'numeric', month:'long', year:'numeric'})}</p>
            <p className="font-bold text-gray-800">Dr {selected.doctor?.user?.name}</p>
            <p className="text-sm text-green-600">{selected.doctor?.user?.specialty}</p>
          </div>

          {selected.symptoms && (
            <div className="card">
              <h3 className="font-bold text-gray-700 mb-2">🤒 Symptômes</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{selected.symptoms}</p>
            </div>
          )}

          {selected.diagnosis && (
            <div className="card">
              <h3 className="font-bold text-gray-700 mb-2">🔍 Diagnostic</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{selected.diagnosis}</p>
            </div>
          )}

          {selected.treatment && (
            <div className="card">
              <h3 className="font-bold text-gray-700 mb-2">💊 Traitement</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{selected.treatment}</p>
            </div>
          )}

          {selected.prescription && (
            <div className="card border-l-4 border-green-500">
              <h3 className="font-bold text-gray-700 mb-3">📋 Ordonnance</h3>
              <div className="space-y-2">
                {(selected.prescription.medications || []).map((med, i) => (
                  <div key={i} className="bg-green-50 rounded-xl p-3">
                    <p className="font-semibold text-sm text-gray-800">{med.name}</p>
                    <p className="text-xs text-gray-600">Dosage: {med.dosage}</p>
                    <p className="text-xs text-gray-600">Durée: {med.duration}</p>
                    {med.notes && <p className="text-xs text-gray-500 mt-1">{med.notes}</p>}
                  </div>
                ))}
              </div>
              {selected.prescription.instructions && (
                <p className="text-sm text-gray-600 mt-3 p-2 bg-yellow-50 rounded-xl">
                  ℹ️ {selected.prescription.instructions}
                </p>
              )}
              {selected.prescription.valid_until && (
                <p className="text-xs text-gray-400 mt-2">Valable jusqu'au {new Date(selected.prescription.valid_until).toLocaleDateString('fr-FR')}</p>
              )}
            </div>
          )}

          {selected.follow_up_date && (
            <div className="card bg-blue-50 border-blue-200">
              <p className="text-sm font-medium text-blue-700">
                📅 Suivi prévu le {new Date(selected.follow_up_date).toLocaleDateString('fr-FR', {day:'numeric', month:'long', year:'numeric'})}
              </p>
            </div>
          )}

          {selected.notes && (
            <div className="card">
              <h3 className="font-bold text-gray-700 mb-2">📝 Notes</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{selected.notes}</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b px-4 py-3 flex items-center gap-3 sticky top-0 z-10">
        <Link to="/patient/dashboard" className="text-green-600 font-medium">← Retour</Link>
        <h1 className="font-bold text-gray-800">Mon dossier médical</h1>
      </div>

      <div className="max-w-lg mx-auto p-4">
        {loading ? (
          <p className="text-center text-gray-400 py-8">Chargement...</p>
        ) : records.length === 0 ? (
          <div className="text-center py-10">
            <p className="text-4xl mb-3">📋</p>
            <p className="text-gray-500">Aucun dossier médical</p>
            <p className="text-sm text-gray-400 mt-1">Vos dossiers apparaîtront après vos consultations</p>
          </div>
        ) : (
          <div className="space-y-3">
            {records.map(record => (
              <button key={record.id} onClick={() => setSelected(record)}
                className="card w-full text-left hover:border-green-300 hover:shadow-md transition-all">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-bold text-gray-800">Dr {record.doctor?.user?.name}</p>
                    <p className="text-sm text-gray-500 mt-0.5">
                      {new Date(record.created_at).toLocaleDateString('fr-FR', {day:'numeric', month:'long', year:'numeric'})}
                    </p>
                    {record.diagnosis && (
                      <p className="text-sm text-gray-600 mt-1 line-clamp-1">🔍 {record.diagnosis}</p>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    {record.prescription && (
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">💊 Ordonnance</span>
                    )}
                    {record.follow_up_date && (
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">📅 Suivi</span>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
